import React, { useState, useEffect, useMemo, useCallback } from 'react';
import QRCode from 'qrcode';
import {
  Smartphone,
  Radio,
  Network,
  ShieldCheck,
  ShieldAlert,
  Layers,
  Wifi,
  Battery,
  BatteryCharging,
  BatteryWarning,
  Activity,
  RefreshCw,
  Copy,
  Check,
  Plus,
  Search,
  Power,
  X,
  AlertTriangle,
  QrCode,
  Send,
  Terminal,
  Monitor,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import MetricCard from './MetricCard';
import {
  nodesService,
  type AdminNodesOverview,
} from '../services/nodesService';
import '../styles/admin-nodes.css';

export const AdminNodesPage: React.FC = () => {
  const { token } = useAuth();
  const { addToast } = useToast();

  const [data, setData] = useState<AdminNodesOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'nodes' | 'pool' | 'instances'>('nodes');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'ALL' | 'PLATFORM_POOL' | 'TENANT_DEDICATED'>('ALL');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Pairing Modal state
  const [isPairingModalOpen, setIsPairingModalOpen] = useState(false);
  const [pairingRole, setPairingRole] = useState<'PLATFORM_POOL' | 'TENANT_DEDICATED'>('PLATFORM_POOL');
  const [pairingTenantId, setPairingTenantId] = useState('');
  const [pairingCountry, setPairingCountry] = useState('VE');
  const [generatingQr, setGeneratingQr] = useState(false);
  const [generatedPairing, setGeneratedPairing] = useState<{
    nodeId: string;
    token: string;
    qrData: string;
    qrDataUrl: string;
  } | null>(null);
  const [pairingTab, setPairingTab] = useState<'android' | 'docker' | 'windows'>('android');

  // Disconnect loading state
  const [disconnectingNodeId, setDisconnectingNodeId] = useState<string | null>(null);

  // WhatsApp QR Modal state
  const [qrModal, setQrModal] = useState<{
    instanceId: string;
    instanceName: string;
    qrUrl?: string;
    status?: string;
    loading: boolean;
  } | null>(null);

  // Test Message Modal state
  const [testMsgModal, setTestMsgModal] = useState<{
    instanceId: string;
    instanceName: string;
    to: string;
    text: string;
    sending: boolean;
  } | null>(null);

  // Reconnect loading state
  const [reconnectingInstanceId, setReconnectingInstanceId] = useState<string | null>(null);

  const fetchOverview = useCallback(
    async (showSilent = false) => {
      if (!showSilent) setLoading(true);
      try {
        const overview = await nodesService.getOverview(token);
        setData(overview);
      } catch (err: unknown) {
        if (!showSilent) {
          addToast({
            type: 'error',
            title: 'Error de Conexión',
            message: err instanceof Error ? err.message : 'No se pudo obtener la telemetría de nodos',
          });
        }
      } finally {
        if (!showSilent) setLoading(false);
      }
    },
    [token, addToast],
  );

  useEffect(() => {
    void fetchOverview();
    const interval = setInterval(() => {
      void fetchOverview(true);
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchOverview]);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    addToast({
      type: 'info',
      title: 'Copiado',
      message: 'Texto copiado al portapapeles',
      duration: 2000,
    });
    setTimeout(() => setCopiedId(null), 2500);
  };

  // Poll QR code when QR modal is open
  useEffect(() => {
    if (!qrModal?.instanceId) return;
    let isCancelled = false;

    const pollQr = async () => {
      try {
        const res = await nodesService.getChannelQr(qrModal.instanceId, token);
        if (isCancelled) return;
        setQrModal((prev) =>
          prev
            ? {
                ...prev,
                qrUrl: res.qrUrl,
                status: res.status,
                loading: false,
              }
            : null,
        );
        if (res.status === 'CONNECTED') {
          addToast({
            type: 'success',
            title: 'WhatsApp Conectado',
            message: 'La instancia se ha vinculado exitosamente.',
          });
          void fetchOverview(true);
        }
      } catch {
        // Silently retry
      }
    };

    void pollQr();
    const interval = setInterval(() => void pollQr(), 4000);
    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [qrModal?.instanceId, token, addToast, fetchOverview]);

  const handleOpenQrModal = (instanceId: string, instanceName: string) => {
    setQrModal({ instanceId, instanceName, loading: true });
  };

  const handleOpenTestModal = (instanceId: string, instanceName: string) => {
    setTestMsgModal({
      instanceId,
      instanceName,
      to: '04149235102',
      text: '¡Hola! Mensaje de prueba desde Iridia Node con salida por proxy local.',
      sending: false,
    });
  };

  const handleReconnectChannel = async (instanceId: string) => {
    setReconnectingInstanceId(instanceId);
    try {
      const res = await nodesService.reconnectChannel(instanceId, token);
      addToast({
        type: 'info',
        title: 'Reconexión Solicitada',
        message: `Estado: ${res.status}. Si requiere escanear QR, usa el botón "Ver QR".`,
      });
      void fetchOverview(true);
    } catch (err: unknown) {
      addToast({
        type: 'error',
        title: 'Error de Reconexión',
        message: err instanceof Error ? err.message : 'No se pudo reiniciar la sesión',
      });
    } finally {
      setReconnectingInstanceId(null);
    }
  };

  const handleSendTestMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testMsgModal) return;
    if (!testMsgModal.to.trim() || !testMsgModal.text.trim()) {
      addToast({
        type: 'error',
        title: 'Validación',
        message: 'Ingresa el número de destino y el mensaje de texto',
      });
      return;
    }

    setTestMsgModal((prev) => (prev ? { ...prev, sending: true } : null));
    try {
      const res = await nodesService.sendTestMessage(
        testMsgModal.instanceId,
        { to: testMsgModal.to, text: testMsgModal.text },
        token,
      );
      addToast({
        type: 'success',
        title: 'Mensaje Enviado',
        message: `Mensaje enviado con éxito a ${res.to} (ID: ${res.messageId})`,
      });
      setTestMsgModal(null);
    } catch (err: unknown) {
      addToast({
        type: 'error',
        title: 'Fallo al Enviar',
        message: err instanceof Error ? err.message : 'No se pudo despachar el mensaje',
      });
      setTestMsgModal((prev) => (prev ? { ...prev, sending: false } : null));
    }
  };

  const handleGeneratePairing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pairingRole === 'TENANT_DEDICATED' && !pairingTenantId.trim()) {
      addToast({
        type: 'error',
        title: 'Validación',
        message: 'Debes ingresar el Tenant ID para un nodo dedicado',
      });
      return;
    }

    setGeneratingQr(true);
    try {
      const res = await nodesService.generatePairingToken(
        {
          role: pairingRole,
          tenantId: pairingRole === 'TENANT_DEDICATED' ? pairingTenantId.trim() : undefined,
          country: pairingCountry,
        },
        token,
      );

      const qrDataUrl = await QRCode.toDataURL(res.qrData, {
        width: 260,
        margin: 2,
        color: {
          dark: '#0f172a',
          light: '#ffffff',
        },
      });

      setGeneratedPairing({
        nodeId: res.nodeId,
        token: res.token,
        qrData: res.qrData,
        qrDataUrl,
      });

      addToast({
        type: 'success',
        title: 'QR Generado',
        message: 'Código de vinculación listo para ser escaneado con la app Iridia Node',
      });
      void fetchOverview(true);
    } catch (err: unknown) {
      addToast({
        type: 'error',
        title: 'Error',
        message: err instanceof Error ? err.message : 'No se pudo generar el código QR de emparejamiento',
      });
    } finally {
      setGeneratingQr(false);
    }
  };

  const handleDisconnect = async (nodeId: string) => {
    if (!window.confirm(`¿Estás seguro de desconectar el nodo ${nodeId}? Si tiene instancias asignadas, pasarán a failover.`)) {
      return;
    }

    setDisconnectingNodeId(nodeId);
    try {
      await nodesService.disconnectNode(nodeId, token);
      addToast({
        type: 'info',
        title: 'Nodo Desconectado',
        message: `El nodo ${nodeId} ha sido desconectado.`,
      });
      void fetchOverview(true);
    } catch (err: unknown) {
      addToast({
        type: 'error',
        title: 'Error',
        message: err instanceof Error ? err.message : 'Fallo al desconectar nodo',
      });
    } finally {
      setDisconnectingNodeId(null);
    }
  };

  // Filtered nodes
  const filteredNodes = useMemo(() => {
    if (!data?.nodes) return [];
    return data.nodes.filter((node) => {
      const matchesSearch =
        node.nodeId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (node.tenantName && node.tenantName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (node.publicIp && node.publicIp.includes(searchQuery));
      const matchesRole = roleFilter === 'ALL' || node.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [data?.nodes, searchQuery, roleFilter]);

  // Filtered instances
  const filteredInstances = useMemo(() => {
    if (!data?.instances) return [];
    return data.instances.filter((inst) => {
      return (
        inst.tenantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inst.instanceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (inst.currentNodeId && inst.currentNodeId.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (inst.proxyHost && inst.proxyHost.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    });
  }, [data?.instances, searchQuery]);

  return (
    <div className="admin-nodes-page">
      {/* Header */}
      <div className="nodes-header-row">
        <div className="nodes-title-area">
          <h1>
            <Radio className="text-blue-600" size={24} /> Nodos Móviles & Red de Proxies (Iridia Node)
          </h1>
          <p className="nodes-subtitle">
            Supervisión en tiempo real de nodos celulares residenciales, salud del pool de contingencia y enrutamiento anti-baneo.
          </p>
        </div>

        <div className="nodes-actions-bar">
          <button
            type="button"
            className="nodes-btn nodes-btn-secondary"
            onClick={() => void fetchOverview()}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
          <button
            type="button"
            className="nodes-btn nodes-btn-primary"
            onClick={() => {
              setGeneratedPairing(null);
              setIsPairingModalOpen(true);
            }}
          >
            <Plus size={16} />
            Vincular Teléfono / Nodo
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="metrics-grid" style={{ marginBottom: '1.5rem' }}>
        <MetricCard
          title="Nodos Conectados"
          value={data?.summary.onlineNodes ?? 0}
          icon="📱"
          trend={data ? `${data.summary.totalNodes} registrados (${data.summary.offlineNodes} offline)` : undefined}
          type={data?.summary.onlineNodes && data.summary.onlineNodes > 0 ? 'success' : 'warning'}
        />
        <MetricCard
          title="Pool de Contingencia"
          value={data?.pool.status ?? 'CARGANDO'}
          icon="🛡️"
          trend={data ? `${data.pool.availableNodesCount} teléfonos (${data.pool.utilizationPercent}% uso)` : undefined}
          type={data?.pool.status === 'HEALTHY' ? 'success' : data?.pool.status === 'DEGRADED' ? 'warning' : 'error'}
        />
        <MetricCard
          title="Streams TCP Activos"
          value={data?.summary.totalActiveStreams ?? 0}
          icon="⚡"
          trend="Túneles WhatsApp en curso"
          type="default"
        />
        <MetricCard
          title="Instancias en Failover"
          value={data?.summary.instancesInFailover ?? 0}
          icon="⚠️"
          trend={data ? `${data.summary.instancesUsingProxies} usan proxies/nodos` : undefined}
          type={data?.summary.instancesInFailover && data.summary.instancesInFailover > 0 ? 'error' : 'default'}
        />
      </div>

      {/* Tabs */}
      <div className="nodes-tabs">
        <button
          type="button"
          className={`nodes-tab-btn ${activeTab === 'nodes' ? 'active' : ''}`}
          onClick={() => setActiveTab('nodes')}
        >
          <Smartphone size={16} />
          Todos los Nodos
          <span className="nodes-tab-badge">{data?.nodes.length ?? 0}</span>
        </button>
        <button
          type="button"
          className={`nodes-tab-btn ${activeTab === 'pool' ? 'active' : ''}`}
          onClick={() => setActiveTab('pool')}
        >
          <Layers size={16} />
          Pool de Contingencia
          <span className="nodes-tab-badge">{data?.pool.nodes.length ?? 0}</span>
        </button>
        <button
          type="button"
          className={`nodes-tab-btn ${activeTab === 'instances' ? 'active' : ''}`}
          onClick={() => setActiveTab('instances')}
        >
          <Network size={16} />
          Instancias WhatsApp & Enrutamiento
          <span className="nodes-tab-badge">{data?.instances.length ?? 0}</span>
        </button>
      </div>

      {/* Tab 1: Todos los Nodos */}
      {activeTab === 'nodes' && (
        <div className="nodes-section-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.25rem', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: '240px' }}>
              <Search size={18} className="text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por Node ID, IP o Negocio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.45rem 0.75rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as any)}
                style={{
                  padding: '0.45rem 0.75rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  backgroundColor: '#ffffff',
                }}
              >
                <option value="ALL">Todos los Roles</option>
                <option value="PLATFORM_POOL">Solo Pool de Contingencia</option>
                <option value="TENANT_DEDICATED">Solo Dedicados de Tenants</option>
              </select>
            </div>
          </div>

          <div className="nodes-table-container">
            <table className="nodes-table">
              <thead>
                <tr>
                  <th>Nodo ID</th>
                  <th>Rol / Asignación</th>
                  <th>Estado</th>
                  <th>IP Pública & Red</th>
                  <th>Batería</th>
                  <th>Streams / Tráfico</th>
                  <th>Último Latido</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredNodes.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                      {loading ? 'Cargando nodos...' : 'No se encontraron nodos registrados.'}
                    </td>
                  </tr>
                ) : (
                  filteredNodes.map((node) => (
                    <tr key={node.nodeId}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{node.nodeId}</span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(node.nodeId, node.nodeId)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                            title="Copiar ID"
                          >
                            {copiedId === node.nodeId ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                          </button>
                        </div>
                      </td>
                      <td>
                        {node.role === 'PLATFORM_POOL' ? (
                          <span className="node-badge badge-pool">
                            <Layers size={12} /> Pool Plataforma
                          </span>
                        ) : (
                          <span className="node-badge badge-tenant">
                            <Smartphone size={12} /> {node.tenantName || node.tenantId || 'Tenant'}
                          </span>
                        )}
                      </td>
                      <td>
                        {node.status === 'ONLINE' ? (
                          <span className="node-badge badge-online">
                            <Activity size={12} /> Online
                          </span>
                        ) : node.status === 'BUSY' ? (
                          <span className="node-badge badge-busy">
                            <Activity size={12} /> Ocupado
                          </span>
                        ) : (
                          <span className="node-badge badge-offline">
                            <Power size={12} /> Offline
                          </span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                          <span style={{ fontWeight: 500 }}>{node.publicIp || 'Sin IP'}</span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            {node.networkType === 'wifi' ? <Wifi size={12} /> : <Activity size={12} />}
                            {node.networkType || 'Desconocida'} · {node.country || 'VE'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          {node.isCharging ? (
                            <BatteryCharging size={16} className="text-green-600" />
                          ) : (node.batteryLevel ?? 100) <= 20 ? (
                            <BatteryWarning size={16} className="text-red-500" />
                          ) : (
                            <Battery size={16} className="text-blue-600" />
                          )}
                          <span style={{ fontWeight: 600 }}>{node.batteryLevel ?? '--'}%</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600 }}>{node.activeStreams} activos</span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {(Number(node.totalBytesTransferred) / 1024 / 1024).toFixed(1)} MB transferidos
                          </span>
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: '0.8rem', color: '#475569' }}>
                          {node.lastHeartbeatAt ? new Date(node.lastHeartbeatAt).toLocaleTimeString() : 'Nunca'}
                        </span>
                      </td>
                      <td>
                        {node.isAliveInMemory ? (
                          <button
                            type="button"
                            className="nodes-btn nodes-btn-danger"
                            onClick={() => handleDisconnect(node.nodeId)}
                            disabled={disconnectingNodeId === node.nodeId}
                          >
                            <Power size={12} /> Desconectar
                          </button>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Inactivo</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 2: Pool de Contingencia */}
      {activeTab === 'pool' && (
        <div>
          {/* Banner de Salud del Pool */}
          <div
            className={`pool-health-banner ${
              data?.pool.status === 'HEALTHY'
                ? 'pool-banner-healthy'
                : data?.pool.status === 'DEGRADED'
                ? 'pool-banner-degraded'
                : 'pool-banner-critical'
            }`}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {data?.pool.status === 'HEALTHY' ? (
                <ShieldCheck size={28} />
              ) : (
                <ShieldAlert size={28} />
              )}
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>
                  Estado del Pool de Contingencia: {data?.pool.status}
                </h3>
                <p style={{ margin: 0, fontSize: '0.85rem' }}>
                  {data?.pool.status === 'HEALTHY'
                    ? 'Capacidad óptima para absorber failovers inmediatos si algún teléfono de cliente se desconecta.'
                    : data?.pool.status === 'DEGRADED'
                    ? 'Atención: Nivel de capacidad ajustado o pocos teléfonos de respaldo en línea.'
                    : 'CRÍTICO: No hay teléfonos disponibles en el pool de contingencia. Conecta al menos un dispositivo.'}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="nodes-btn nodes-btn-primary"
              onClick={() => {
                setPairingRole('PLATFORM_POOL');
                setGeneratedPairing(null);
                setIsPairingModalOpen(true);
              }}
            >
              <Plus size={16} /> Añadir Teléfono al Pool
            </button>
          </div>

          {/* Utilización de Capacidad */}
          <div className="nodes-section-card">
            <h3 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              Utilización de Capacidad del Pool
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
              {data?.pool.activeStreams ?? 0} de {data?.pool.totalCapacityStreams ?? 0} streams concurrentes en uso
              ({data?.pool.utilizationPercent ?? 0}%)
            </p>
            <div className="pool-progress-track">
              <div
                className="pool-progress-fill"
                style={{
                  width: `${data?.pool.utilizationPercent ?? 0}%`,
                  backgroundColor:
                    (data?.pool.utilizationPercent ?? 0) > 80
                      ? '#ef4444'
                      : (data?.pool.utilizationPercent ?? 0) > 50
                      ? '#f59e0b'
                      : '#2563eb',
                }}
              />
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                Teléfonos Asignados al Pool ({data?.pool.nodes.length ?? 0})
              </h4>
              <div className="nodes-table-container">
                <table className="nodes-table">
                  <thead>
                    <tr>
                      <th>Nodo ID</th>
                      <th>Estado</th>
                      <th>IP & País</th>
                      <th>Batería</th>
                      <th>Streams Activos</th>
                      <th>Último Latido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!data?.pool.nodes || data.pool.nodes.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                          No hay teléfonos asignados al pool de contingencia aún.
                        </td>
                      </tr>
                    ) : (
                      data.pool.nodes.map((node) => (
                        <tr key={node.nodeId}>
                          <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{node.nodeId}</td>
                          <td>
                            {node.status === 'ONLINE' ? (
                              <span className="node-badge badge-online">Online</span>
                            ) : (
                              <span className="node-badge badge-offline">Offline</span>
                            )}
                          </td>
                          <td>
                            {node.publicIp || 'Sin IP'} ({node.country || 'VE'})
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              {node.isCharging ? <BatteryCharging size={14} className="text-green-600" /> : <Battery size={14} />}
                              {node.batteryLevel ?? '--'}%
                            </div>
                          </td>
                          <td>{node.activeStreams} streams</td>
                          <td>{node.lastHeartbeatAt ? new Date(node.lastHeartbeatAt).toLocaleTimeString() : 'Nunca'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Instancias WhatsApp & Enrutamiento */}
      {activeTab === 'instances' && (
        <div className="nodes-section-card">
          <div style={{ marginBottom: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 600 }}>
              Instancias WhatsApp y Modo de Enrutamiento (Egress)
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '300px' }}>
              <Search size={16} className="text-gray-400" />
              <input
                type="text"
                placeholder="Buscar instancia o tenant..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.4rem 0.75rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                }}
              />
            </div>
          </div>

          <div className="nodes-table-container">
            <table className="nodes-table">
              <thead>
                <tr>
                  <th>Negocio / Tenant</th>
                  <th>Instancia WhatsApp</th>
                  <th>Estado WA</th>
                  <th>Modo de Enrutamiento Egress</th>
                  <th>Nodo / Proxy Asignado</th>
                  <th>Alerta de Failover</th>
                  <th>Última Actividad</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredInstances.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                      No se encontraron instancias de WhatsApp configuradas.
                    </td>
                  </tr>
                ) : (
                  filteredInstances.map((inst) => (
                    <tr key={inst.id}>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600 }}>{inst.tenantName}</span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{inst.tenantSlug || inst.tenantId}</span>
                        </div>
                      </td>
                      <td style={{ fontWeight: 500 }}>{inst.instanceName}</td>
                      <td>
                        <span
                          className={`node-badge ${
                            inst.status === 'CONNECTED'
                              ? 'badge-online'
                              : inst.status === 'CONNECTING'
                              ? 'badge-busy'
                              : 'badge-offline'
                          }`}
                        >
                          {inst.status}
                        </span>
                      </td>
                      <td>
                        {inst.routingMode === 'POOL_FAILOVER' ? (
                          <span className="node-badge badge-failover">
                            <AlertTriangle size={12} /> {inst.currentNodeId === 'DIRECT_SERVER_IP' ? 'FAILOVER SERVIDOR' : 'FAILOVER AL POOL'}
                          </span>
                        ) : inst.routingMode === 'IRIDIA_NODE' ? (
                          <span className="node-badge badge-tenant">
                            <Smartphone size={12} /> Iridia Node Móvil
                          </span>
                        ) : inst.routingMode === 'CUSTOM_PROXY' ? (
                          <span className="node-badge badge-pool">
                            <Network size={12} /> Proxy Residencial
                          </span>
                        ) : (
                          <span className="node-badge badge-offline">Directo (Servidor)</span>
                        )}
                      </td>
                      <td>
                        {inst.routingMode === 'POOL_FAILOVER' ? (
                          <div>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#dc2626' }}>
                              {inst.currentNodeId === 'DIRECT_SERVER_IP'
                                ? 'IP Servidor Directa'
                                : `${inst.currentNodeId} (Pool)`}
                            </span>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Primario: {inst.primaryNodeId}</div>
                          </div>
                        ) : inst.routingMode === 'IRIDIA_NODE' ? (
                          <div>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{inst.currentNodeId}</span>
                            <div style={{ fontSize: '0.75rem', color: inst.nodeStatus === 'ONLINE' ? '#16a34a' : '#dc2626' }}>
                              {inst.nodeStatus === 'ONLINE' ? `En línea (${inst.nodeBattery ?? '--'}%)` : 'Desconectado'}
                            </div>
                          </div>
                        ) : inst.routingMode === 'CUSTOM_PROXY' ? (
                          <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{inst.proxyHost}</span>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Sin Proxy</span>
                        )}
                      </td>
                      <td>
                        {inst.isFailoverActive ? (
                          <div style={{ color: '#b91c1c', fontSize: '0.8rem' }}>
                            <div style={{ fontWeight: 600 }}>{inst.failoverReason || 'Timeout'}</div>
                            <div>{inst.failedAt ? new Date(inst.failedAt).toLocaleTimeString() : ''}</div>
                          </div>
                        ) : (
                          <span style={{ color: '#16a34a', fontSize: '0.8rem' }}>Normal</span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {inst.lastActiveAt ? new Date(inst.lastActiveAt).toLocaleString() : 'N/A'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'nowrap' }}>
                          {inst.channelType === 'WHATSAPP' && (
                            <button
                              type="button"
                              className="nodes-btn nodes-btn-secondary"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                              onClick={() => handleReconnectChannel(inst.id)}
                              disabled={reconnectingInstanceId === inst.id}
                              title="Reiniciar / Reconectar sesión de WhatsApp"
                            >
                              <RefreshCw size={13} className={reconnectingInstanceId === inst.id ? 'animate-spin' : ''} />
                              Reconectar
                            </button>
                          )}
                          {inst.channelType === 'WHATSAPP' && inst.status !== 'CONNECTED' && (
                            <button
                              type="button"
                              className="nodes-btn nodes-btn-secondary"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                              onClick={() => handleOpenQrModal(inst.id, inst.instanceName)}
                              title="Vincular WhatsApp / Ver Código QR"
                            >
                              <QrCode size={13} /> Ver QR
                            </button>
                          )}
                          <button
                            type="button"
                            className="nodes-btn nodes-btn-primary"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                            onClick={() => handleOpenTestModal(inst.id, inst.instanceName)}
                            title="Probar envío a número de teléfono"
                          >
                            <Send size={13} /> Probar Envío
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: Vincular Teléfono / Generar QR */}
      {isPairingModalOpen && (
        <div className="nodes-modal-overlay" onClick={() => setIsPairingModalOpen(false)}>
          <div className="nodes-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="nodes-modal-header">
              <h3 className="nodes-modal-title">Vincular Teléfono Móvil (Iridia Node)</h3>
              <button
                type="button"
                className="nodes-modal-close"
                onClick={() => setIsPairingModalOpen(false)}
              >
                <X size={20} />
              </button>
            </div>

            {!generatedPairing ? (
              <form onSubmit={handleGeneratePairing}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>
                    Rol del Dispositivo Móvil
                  </label>
                  <select
                    value={pairingRole}
                    onChange={(e) => setPairingRole(e.target.value as any)}
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                    }}
                  >
                    <option value="PLATFORM_POOL">Pool de Contingencia de la Plataforma (Global)</option>
                    <option value="TENANT_DEDICATED">Dedicado para un Negocio (Tenant Específico)</option>
                  </select>
                  <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                    {pairingRole === 'PLATFORM_POOL'
                      ? 'Este teléfono respaldará a cualquier cliente si su conexión primaria se cae.'
                      : 'El tráfico de WhatsApp de este negocio saldrá exclusivamente por la IP de este teléfono.'}
                  </p>
                </div>

                {pairingRole === 'TENANT_DEDICATED' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>
                      Tenant ID del Negocio
                    </label>
                    <input
                      type="text"
                      placeholder="cmv5kqw8e0000myh8sqh39coy"
                      value={pairingTenantId}
                      onChange={(e) => setPairingTenantId(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                      }}
                    />
                  </div>
                )}

                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>
                    País de Operación
                  </label>
                  <select
                    value={pairingCountry}
                    onChange={(e) => setPairingCountry(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                    }}
                  >
                    <option value="VE">Venezuela (VE)</option>
                    <option value="CO">Colombia (CO)</option>
                    <option value="US">Estados Unidos (US)</option>
                  </select>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                  <button
                    type="button"
                    className="nodes-btn nodes-btn-secondary"
                    onClick={() => setIsPairingModalOpen(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="nodes-btn nodes-btn-primary"
                    disabled={generatingQr}
                  >
                    {generatingQr ? 'Generando...' : 'Generar Código QR'}
                  </button>
                </div>
              </form>
            ) : (
              <div>
                {/* Selector de método de despliegue */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                  <button
                    type="button"
                    className={`nodes-btn ${pairingTab === 'android' ? 'nodes-btn-primary' : 'nodes-btn-secondary'}`}
                    style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                    onClick={() => setPairingTab('android')}
                  >
                    <Smartphone size={14} /> Android (QR)
                  </button>
                  <button
                    type="button"
                    className={`nodes-btn ${pairingTab === 'docker' ? 'nodes-btn-primary' : 'nodes-btn-secondary'}`}
                    style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                    onClick={() => setPairingTab('docker')}
                  >
                    <Terminal size={14} /> Linux (Docker)
                  </button>
                  <button
                    type="button"
                    className={`nodes-btn ${pairingTab === 'windows' ? 'nodes-btn-primary' : 'nodes-btn-secondary'}`}
                    style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                    onClick={() => setPairingTab('windows')}
                  >
                    <Monitor size={14} /> Windows PC
                  </button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                    Node ID: <code style={{ fontFamily: 'monospace', color: '#2563eb' }}>{generatedPairing.nodeId}</code>
                  </span>
                  <span className="node-badge badge-pool" style={{ fontSize: '0.75rem' }}>
                    {pairingRole === 'PLATFORM_POOL' ? 'Pool Global' : 'Tenant Dedicado'}
                  </span>
                </div>

                {pairingTab === 'android' && (
                  <div>
                    <div className="nodes-qr-container">
                      <img
                        src={generatedPairing.qrDataUrl}
                        alt="QR de Vinculación Iridia Node"
                        className="nodes-qr-image"
                      />
                    </div>
                    <div style={{ backgroundColor: '#f1f5f9', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem' }}>
                      <h4 style={{ fontSize: '0.85rem', fontWeight: 600, margin: '0 0 0.35rem 0' }}>Instrucciones Android:</h4>
                      <ol style={{ fontSize: '0.8rem', color: '#475569', paddingLeft: '1.25rem', margin: 0 }}>
                        <li>Instala el APK de <strong>Iridia Node</strong> en el teléfono.</li>
                        <li>Abre la app y toca <strong>"Escanear QR de Vinculación"</strong>.</li>
                        <li>Apunta la cámara a este código QR para conectar el túnel.</li>
                      </ol>
                    </div>
                  </div>
                )}

                {pairingTab === 'docker' && (
                  <div>
                    <div style={{ backgroundColor: '#0f172a', color: '#f8fafc', padding: '0.85rem', borderRadius: '8px', marginBottom: '0.75rem', position: 'relative' }}>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.35rem' }}>Comando Docker (Ejecución en 1 comando):</div>
                      <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace' }}>
{`./run-docker.sh --nodeId ${generatedPairing.nodeId} --token ${generatedPairing.token}`}
                      </pre>
                      <button
                        type="button"
                        className="nodes-btn nodes-btn-secondary"
                        style={{ marginTop: '0.5rem', padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                        onClick={() => copyToClipboard(`./run-docker.sh --nodeId ${generatedPairing.nodeId} --token ${generatedPairing.token}`, 'docker-cmd')}
                      >
                        {copiedId === 'docker-cmd' ? <Check size={12} className="text-green-500" /> : <Copy size={12} />} Copiar Comando Docker
                      </button>
                    </div>
                    <div style={{ backgroundColor: '#f1f5f9', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem' }}>
                      <h4 style={{ fontSize: '0.85rem', fontWeight: 600, margin: '0 0 0.35rem 0' }}>Instrucciones Linux / Docker:</h4>
                      <ol style={{ fontSize: '0.8rem', color: '#475569', paddingLeft: '1.25rem', margin: 0 }}>
                        <li>En tu servidor Ubuntu/Debian con Docker instalado, clona o entra a la carpeta <code>iridia-node</code>.</li>
                        <li>Ejecuta el comando copiado arriba.</li>
                        <li>El contenedor <code>iridia-node</code> arrancará automáticamente en segundo plano con reinicio continuo.</li>
                      </ol>
                    </div>
                  </div>
                )}

                {pairingTab === 'windows' && (
                  <div>
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', padding: '0.85rem', borderRadius: '8px', marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.75rem', color: '#475569', marginBottom: '0.35rem', fontWeight: 600 }}>Comando Batch para Windows:</div>
                      <code style={{ display: 'block', padding: '0.5rem', backgroundColor: '#e2e8f0', borderRadius: '6px', fontSize: '0.75rem', wordBreak: 'break-all', fontFamily: 'monospace', marginBottom: '0.5rem' }}>
                        start-node.bat --nodeId {generatedPairing.nodeId} --token {generatedPairing.token.slice(0, 35)}...
                      </code>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="nodes-btn nodes-btn-secondary"
                          style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                          onClick={() => copyToClipboard(`start-node.bat --nodeId ${generatedPairing.nodeId} --token ${generatedPairing.token}`, 'win-cmd')}
                        >
                          {copiedId === 'win-cmd' ? <Check size={12} className="text-green-500" /> : <Copy size={12} />} Copiar Comando
                        </button>
                        <button
                          type="button"
                          className="nodes-btn nodes-btn-secondary"
                          style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                          onClick={() => copyToClipboard(generatedPairing.token, 'win-token')}
                        >
                          {copiedId === 'win-token' ? <Check size={12} className="text-green-500" /> : <Copy size={12} />} Copiar Token JWT
                        </button>
                      </div>
                    </div>
                    <div style={{ backgroundColor: '#f1f5f9', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem' }}>
                      <h4 style={{ fontSize: '0.85rem', fontWeight: 600, margin: '0 0 0.35rem 0' }}>Instrucciones Windows PC (Directo):</h4>
                      <ol style={{ fontSize: '0.8rem', color: '#475569', paddingLeft: '1.25rem', margin: 0 }}>
                        <li>Descarga o abre la carpeta <code>iridia-node</code> en tu computadora con Windows 10/11.</li>
                        <li>Haz doble clic en <code>start-node.bat</code> y pega este token si te lo solicita.</li>
                        <li><strong>Opcional para 24/7:</strong> Haz doble clic en <code>install-startup.bat</code> para que inicie solo cada vez que se encienda la PC.</li>
                      </ol>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                  <button
                    type="button"
                    className="nodes-btn nodes-btn-primary"
                    onClick={() => setIsPairingModalOpen(false)}
                  >
                    Finalizar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: Ver Código QR de WhatsApp */}
      {qrModal && (
        <div className="nodes-modal-overlay" onClick={() => setQrModal(null)}>
          <div className="nodes-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="nodes-modal-header">
              <h3 className="nodes-modal-title">Código QR de WhatsApp - {qrModal.instanceName}</h3>
              <button
                type="button"
                className="nodes-modal-close"
                onClick={() => setQrModal(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              {qrModal.status === 'CONNECTED' ? (
                <div style={{ padding: '2rem 1rem' }}>
                  <div style={{ color: '#16a34a', fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    ¡WhatsApp Conectado Exitosamente!
                  </div>
                  <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
                    La sesión está activa y el tráfico sale a través del nodo asignado.
                  </p>
                </div>
              ) : qrModal.loading || !qrModal.qrUrl ? (
                <div style={{ padding: '3rem 1rem', color: '#64748b' }}>
                  <RefreshCw className="animate-spin" size={32} style={{ margin: '0 auto 1rem' }} />
                  <div>Obteniendo código QR de WhatsApp Web...</div>
                </div>
              ) : (
                <div className="nodes-qr-container">
                  <img
                    src={qrModal.qrUrl}
                    alt="QR WhatsApp"
                    className="nodes-qr-image"
                    style={{ width: '260px', height: '260px' }}
                  />
                  <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#475569' }}>
                    Abre WhatsApp en tu teléfono &gt; Dispositivos vinculados &gt; Vincular un dispositivo.
                  </div>
                  <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                    El código se actualiza automáticamente en segundo plano.
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
              <button
                type="button"
                className="nodes-btn nodes-btn-secondary"
                onClick={() => setQrModal(null)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Probar Envío de Mensaje */}
      {testMsgModal && (
        <div className="nodes-modal-overlay" onClick={() => setTestMsgModal(null)}>
          <div className="nodes-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="nodes-modal-header">
              <h3 className="nodes-modal-title">Probar Envío de Mensaje - {testMsgModal.instanceName}</h3>
              <button
                type="button"
                className="nodes-modal-close"
                onClick={() => setTestMsgModal(null)}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSendTestMessage}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>
                  Número de Teléfono Destino (WhatsApp)
                </label>
                <input
                  type="text"
                  placeholder="Ej: 04149235102 o 584149235102"
                  value={testMsgModal.to}
                  onChange={(e) => setTestMsgModal({ ...testMsgModal, to: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>
                  Mensaje de Texto
                </label>
                <textarea
                  rows={3}
                  value={testMsgModal.text}
                  onChange={(e) => setTestMsgModal({ ...testMsgModal, text: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                  }}
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button
                  type="button"
                  className="nodes-btn nodes-btn-secondary"
                  onClick={() => setTestMsgModal(null)}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="nodes-btn nodes-btn-primary"
                  disabled={testMsgModal.sending}
                >
                  {testMsgModal.sending ? (
                    <>
                      <RefreshCw className="animate-spin" size={14} /> Enviando...
                    </>
                  ) : (
                    <>
                      <Send size={14} /> Enviar Mensaje
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNodesPage;
